import {
  applyPointProjectPropsToWaypoints,
  captureState as captureImportStateSnapshot
} from './portal-import-state-utils.js'
import {
  serializeXmlDocumentPretty
} from './portal-xml-utils.js'
import {
  startPortalLoadingTask
} from './portal-loading-widget.js'
import {
  offsetLatLngByMeters,
  bearingBetweenLatLng,
  angleDiffDeg
} from './portal-geo-utils.js'
import {
  decorateLeafletPathDualDash
} from './portal-dual-dash.js'

const hasMissionRenderLayerContext = function (ctx) {
  return !!(
    ctx &&
    ctx.importItems &&
    typeof ctx.importItems.clearLayers === 'function' &&
    typeof ctx.importItems.addLayer === 'function' &&
    ctx.importLineItems &&
    typeof ctx.importLineItems.clearLayers === 'function' &&
    typeof ctx.importLineItems.addLayer === 'function'
  )
}

const SHADOW_CENTER_KEY = 'K'
const SHADOW_LEFT_KEY = 'B'
const SHADOW_RIGHT_KEY = 'J'
const SHADOW_KEY_ORDER = [SHADOW_LEFT_KEY, SHADOW_CENTER_KEY, SHADOW_RIGHT_KEY]
const SHADOW_COLOR_BY_KEY = {
  B: '#0d6efd',
  K: '#6f42c1',
  J: '#dc3545'
}

let shadowBindingsInstalled = false
let latestMissionCtx = null
let dragDistanceLabels = []
let shiftPreviewLayers = []
let shiftPreviewLabels = []
let shiftPreviewTargets = []
let shiftPreviewInsertionContext = null
let shiftPreviewCursorLatLng = null
let shiftPreviewLabelDebounceTimer = null
let shiftPreviewPendingLabelPayload = null
let lastInsertPreviewDebug = null
let lastInsertProbeLatLng = null
let missionRenderCtx = null
let shiftHandlerInstalled = false
const INSERT_PREVIEW_RELEASE_ANGLE_MIN_DEG = 165
const INSERT_PREVIEW_SINGLE_TARGET_ANGLE_MAX_DEG = 5
const INSERT_PREVIEW_CLICK_SNAP_PX = 18
const INSERT_PREVIEW_GRID_SNAP_PX = 14
const SHIFT_PREVIEW_LABEL_BASE_RADIUS_MULTIPLIER = 3
const SHIFT_PREVIEW_LABEL_DEBOUNCE_MS = 100
const SHIFT_PREVIEW_LABEL_PUSH_STEP_PX = 8
const SHIFT_PREVIEW_LABEL_MAX_PUSH_STEPS = 12

const clearShiftPreviewVisuals = function () {
  if (!missionRenderCtx) return
  shiftPreviewLayers.forEach(function (l) { missionRenderCtx.map.removeLayer(l) })
  shiftPreviewLayers = []
  shiftPreviewLabels.forEach(function (l) { missionRenderCtx.map.removeLayer(l) })
  shiftPreviewLabels = []
}

const clearShiftPreviewDebounce = function () {
  if (shiftPreviewLabelDebounceTimer) {
    clearTimeout(shiftPreviewLabelDebounceTimer)
    shiftPreviewLabelDebounceTimer = null
  }
  shiftPreviewPendingLabelPayload = null
}

const clearShiftPreview = function () {
  clearShiftPreviewDebounce()
  clearShiftPreviewVisuals()
  shiftPreviewTargets = []
  shiftPreviewInsertionContext = null
  shiftPreviewCursorLatLng = null
  lastInsertPreviewDebug = null
}

const buildLabelBounds = function (pointPx, text) {
  const txt = String(text || '')
  const width = Math.max(26, (txt.length * 8.2))
  const height = 16
  return {
    left: Number(pointPx.x) - (width / 2),
    right: Number(pointPx.x) + (width / 2),
    top: Number(pointPx.y) - (height / 2),
    bottom: Number(pointPx.y) + (height / 2)
  }
}

const labelBoundsOverlap = function (a, b) {
  return !(
    a.right < b.left ||
    b.right < a.left ||
    a.bottom < b.top ||
    b.bottom < a.top
  )
}

const renderShiftPreviewLabels = function (payload) {
  if (!missionRenderCtx || !missionRenderCtx.map || !missionRenderCtx.L || !payload) return
  const map = missionRenderCtx.map
  const L = missionRenderCtx.L
  clearShiftPreviewVisuals()
  const rows = Array.isArray(payload.rows) ? payload.rows : []
  rows.forEach(function (row) {
    if (!row || !row.ptLL || !row.cursorLL) return
    const line = decorateLeafletPathDualDash(L.polyline([row.cursorLL, row.ptLL], {
      color: '#000000',
      weight: 2,
      opacity: 0.85,
      dashArray: '5, 5',
      interactive: false,
      className: 'portal-import-line--shift-preview'
    }), { L: L })
    line.addTo(map)
    shiftPreviewLayers.push(line)
  })
  const validRows = rows
    .map(function (row) {
      if (!row || !row.ptLL || !row.cursorLL) return null
      const cursorPoint = map.latLngToContainerPoint(row.cursorLL)
      const targetPoint = map.latLngToContainerPoint(row.ptLL)
      const dx = Number(targetPoint.x) - Number(cursorPoint.x)
      const dy = Number(targetPoint.y) - Number(cursorPoint.y)
      const lineLenPx = Math.hypot(dx, dy)
      if (!(lineLenPx > 0.0001)) return null
      const unitX = dx / lineLenPx
      const unitY = dy / lineLenPx
      const demCursorBadgeOffsetPx = Math.hypot(20, 20)
      const maxAlongPx = Math.max(8, lineLenPx - 8)
      const baseAlongPx = Math.min(
        Math.max(demCursorBadgeOffsetPx * SHIFT_PREVIEW_LABEL_BASE_RADIUS_MULTIPLIER, 8),
        maxAlongPx
      )
      return {
        text: String(row.text || ''),
        cursorPoint: cursorPoint,
        unitX: unitX,
        unitY: unitY,
        lineLenPx: lineLenPx,
        maxAlongPx: maxAlongPx,
        alongPx: baseAlongPx
      }
    })
    .filter(Boolean)

  if (validRows.length === 2) {
    let steps = 0
    while (steps < SHIFT_PREVIEW_LABEL_MAX_PUSH_STEPS) {
      const p0 = L.point(
        Number(validRows[0].cursorPoint.x) + (validRows[0].unitX * validRows[0].alongPx),
        Number(validRows[0].cursorPoint.y) + (validRows[0].unitY * validRows[0].alongPx)
      )
      const p1 = L.point(
        Number(validRows[1].cursorPoint.x) + (validRows[1].unitX * validRows[1].alongPx),
        Number(validRows[1].cursorPoint.y) + (validRows[1].unitY * validRows[1].alongPx)
      )
      const overlap = labelBoundsOverlap(
        buildLabelBounds(p0, validRows[0].text),
        buildLabelBounds(p1, validRows[1].text)
      )
      if (!overlap) break
      const next0 = Math.min(validRows[0].maxAlongPx, validRows[0].alongPx + SHIFT_PREVIEW_LABEL_PUSH_STEP_PX)
      const next1 = Math.min(validRows[1].maxAlongPx, validRows[1].alongPx + SHIFT_PREVIEW_LABEL_PUSH_STEP_PX)
      const progressed = (next0 > validRows[0].alongPx) || (next1 > validRows[1].alongPx)
      validRows[0].alongPx = next0
      validRows[1].alongPx = next1
      if (!progressed) break
      steps += 1
    }
    const finalP0 = L.point(
      Number(validRows[0].cursorPoint.x) + (validRows[0].unitX * validRows[0].alongPx),
      Number(validRows[0].cursorPoint.y) + (validRows[0].unitY * validRows[0].alongPx)
    )
    const finalP1 = L.point(
      Number(validRows[1].cursorPoint.x) + (validRows[1].unitX * validRows[1].alongPx),
      Number(validRows[1].cursorPoint.y) + (validRows[1].unitY * validRows[1].alongPx)
    )
    const stillOverlaps = labelBoundsOverlap(
      buildLabelBounds(finalP0, validRows[0].text),
      buildLabelBounds(finalP1, validRows[1].text)
    )
    if (stillOverlaps) {
      const hideIndex = validRows[0].lineLenPx <= validRows[1].lineLenPx ? 0 : 1
      validRows[hideIndex].hidden = true
    }
  }

  validRows.forEach(function (row) {
    if (row.hidden) return
    const pointPx = L.point(
      Number(row.cursorPoint.x) + (row.unitX * row.alongPx),
      Number(row.cursorPoint.y) + (row.unitY * row.alongPx)
    )
    const labelLL = map.containerPointToLatLng(pointPx)
    const lbl = L.marker(labelLL, {
      icon: L.divIcon({
        className: 'portal-drag-distance-label',
        html: row.text,
        iconSize: [0, 0],
        iconAnchor: [0, 0]
      }),
      interactive: false,
      keyboard: false
    })
    lbl.addTo(map)
    shiftPreviewLabels.push(lbl)
  })
}

const scheduleShiftPreviewLabelRender = function (payload) {
  shiftPreviewPendingLabelPayload = payload || null
  if (shiftPreviewLabelDebounceTimer) return
  shiftPreviewLabelDebounceTimer = setTimeout(function () {
    shiftPreviewLabelDebounceTimer = null
    const latestPayload = shiftPreviewPendingLabelPayload
    shiftPreviewPendingLabelPayload = null
    if (!latestPayload) return
    renderShiftPreviewLabels(latestPayload)
  }, SHIFT_PREVIEW_LABEL_DEBOUNCE_MS)
}

const getWaypointLeafletLatLng = function (point, ctx) {
  if (!ctx || !ctx.L || !point || !Array.isArray(point.latlng)) return null
  const lat = Number(point.latlng[0])
  const lng = Number(point.latlng[1])
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return ctx.L.latLng(lat, lng)
}

const resolveProjectionRatioOnSegment = function (startPoint, endPoint, latlng, ctx) {
  if (!ctx || !ctx.map || !latlng) return null
  const startLatLng = getWaypointLeafletLatLng(startPoint, ctx)
  const endLatLng = getWaypointLeafletLatLng(endPoint, ctx)
  if (!startLatLng || !endLatLng) return null
  const clickPoint = ctx.map.latLngToLayerPoint(latlng)
  const startLayerPoint = ctx.map.latLngToLayerPoint(startLatLng)
  const endLayerPoint = ctx.map.latLngToLayerPoint(endLatLng)
  const vx = endLayerPoint.x - startLayerPoint.x
  const vy = endLayerPoint.y - startLayerPoint.y
  const wx = clickPoint.x - startLayerPoint.x
  const wy = clickPoint.y - startLayerPoint.y
  const vv = (vx * vx) + (vy * vy)
  if (!(vv > 0)) return null
  return Math.max(0, Math.min(1, ((wx * vx) + (wy * vy)) / vv))
}

const resolvePreviewAngleDeg = function (targetPoints, latlng, ctx) {
  const targets = Array.isArray(targetPoints) ? targetPoints.filter(Boolean) : []
  if (targets.length !== 2 || !latlng || !ctx || !ctx.L) return null
  const firstLatLng = getWaypointLeafletLatLng(targets[0], ctx)
  const secondLatLng = getWaypointLeafletLatLng(targets[1], ctx)
  if (!firstLatLng || !secondLatLng) return null
  const cursorLat = Number(latlng.lat)
  const cursorLng = Number(latlng.lng)
  if (!Number.isFinite(cursorLat) || !Number.isFinite(cursorLng)) return null
  const firstBearing = bearingBetweenLatLng(cursorLat, cursorLng, firstLatLng.lat, firstLatLng.lng)
  const secondBearing = bearingBetweenLatLng(cursorLat, cursorLng, secondLatLng.lat, secondLatLng.lng)
  return angleDiffDeg(firstBearing, secondBearing)
}

const buildSingleTargetInsertionContext = function (points, selectedTarget, insertAfterTarget, latlng, ctx, options) {
  const opts = (options && typeof options === 'object') ? options : {}
  const targetIndex = points.indexOf(selectedTarget)
  if (targetIndex < 0) return null
  const segmentStart = Object.prototype.hasOwnProperty.call(opts, 'segmentStart')
    ? (opts.segmentStart || null)
    : (insertAfterTarget ? selectedTarget : (points[targetIndex - 1] || null))
  const segmentEnd = Object.prototype.hasOwnProperty.call(opts, 'segmentEnd')
    ? (opts.segmentEnd || null)
    : (insertAfterTarget ? (points[targetIndex + 1] || null) : selectedTarget)
  let ratio = null
  if (segmentStart && segmentEnd) {
    ratio = resolveProjectionRatioOnSegment(segmentStart, segmentEnd, latlng, ctx)
  }
  return {
    insertIndex: insertAfterTarget ? (targetIndex + 1) : targetIndex,
    basePoint: selectedTarget,
    segmentStart: segmentStart,
    segmentEnd: segmentEnd,
    ratio: ratio,
    previewTargets: [selectedTarget],
    angleMode: 'single-target'
  }
}

const resolveActiveInsertionContext = function (waypoints, activePoint, latlng, ctx) {
  const points = Array.isArray(waypoints) ? waypoints : []
  if (!points.length || !activePoint || !latlng || !ctx || !ctx.map || !ctx.L) return null
  const activeIndex = points.indexOf(activePoint)
  if (activeIndex < 0) return null
  const prevPoint = activeIndex > 0 ? points[activeIndex - 1] : null
  const nextPoint = activeIndex < points.length - 1 ? points[activeIndex + 1] : null
  if (!prevPoint && !nextPoint) return null
  if (!prevPoint) {
    return {
      insertIndex: 0,
      basePoint: activePoint,
      segmentStart: activePoint,
      segmentEnd: nextPoint,
      ratio: resolveProjectionRatioOnSegment(activePoint, nextPoint, latlng, ctx),
      previewTargets: [activePoint]
    }
  }
  if (!nextPoint) {
    return {
      insertIndex: points.length,
      basePoint: activePoint,
      segmentStart: prevPoint,
      segmentEnd: activePoint,
      ratio: resolveProjectionRatioOnSegment(prevPoint, activePoint, latlng, ctx),
      previewTargets: [activePoint]
    }
  }
  const cursorLatLng = ctx.L.latLng(latlng.lat, latlng.lng)
  const activeLatLng = getWaypointLeafletLatLng(activePoint, ctx)
  const prevLatLng = getWaypointLeafletLatLng(prevPoint, ctx)
  const nextLatLng = getWaypointLeafletLatLng(nextPoint, ctx)
  if (!activeLatLng || !prevLatLng || !nextLatLng) return null
  const distToActive = ctx.map.distance(cursorLatLng, activeLatLng)
  const distToPrev = ctx.map.distance(cursorLatLng, prevLatLng)
  const distToNext = ctx.map.distance(cursorLatLng, nextLatLng)
  const usePrevSide = distToPrev <= distToNext
  const previewTargets = usePrevSide ? [activePoint, prevPoint] : [activePoint, nextPoint]
  const previewAngleDeg = resolvePreviewAngleDeg(previewTargets, latlng, ctx)
  if (Number.isFinite(previewAngleDeg) && previewAngleDeg >= INSERT_PREVIEW_RELEASE_ANGLE_MIN_DEG) {
    setInsertPreviewDebugState({
      angleDeg: previewAngleDeg,
      mode: 'fallback-180',
      targetIndexes: previewTargets.map(function (point) { return points.indexOf(point) }),
      usePrevSide: usePrevSide
    })
    return null
  }
  if (Number.isFinite(previewAngleDeg) && previewAngleDeg < INSERT_PREVIEW_SINGLE_TARGET_ANGLE_MAX_DEG) {
    if (usePrevSide) {
      const selectedTarget = distToPrev <= distToActive ? prevPoint : activePoint
      setInsertPreviewDebugState({
        angleDeg: previewAngleDeg,
        mode: 'single-target-0',
        targetIndexes: previewTargets.map(function (point) { return points.indexOf(point) }),
        selectedTargetIndex: points.indexOf(selectedTarget),
        usePrevSide: usePrevSide
      })
      return buildSingleTargetInsertionContext(points, selectedTarget, selectedTarget === activePoint, latlng, ctx)
    }
    const selectedTarget = distToNext < distToActive ? nextPoint : activePoint
    setInsertPreviewDebugState({
      angleDeg: previewAngleDeg,
      mode: 'single-target-0',
      targetIndexes: previewTargets.map(function (point) { return points.indexOf(point) }),
      selectedTargetIndex: points.indexOf(selectedTarget),
      usePrevSide: usePrevSide
    })
    return buildSingleTargetInsertionContext(points, selectedTarget, selectedTarget === nextPoint, latlng, ctx)
  }
  setInsertPreviewDebugState({
    angleDeg: previewAngleDeg,
    mode: 'dual-preview',
    targetIndexes: previewTargets.map(function (point) { return points.indexOf(point) }),
    usePrevSide: usePrevSide
  })
  if (usePrevSide) {
    return {
      insertIndex: activeIndex,
      basePoint: prevPoint,
      segmentStart: prevPoint,
      segmentEnd: activePoint,
      ratio: resolveProjectionRatioOnSegment(prevPoint, activePoint, latlng, ctx),
      previewTargets: [activePoint, prevPoint]
    }
  }
  return {
    insertIndex: activeIndex + 1,
    basePoint: activePoint,
    segmentStart: activePoint,
    segmentEnd: nextPoint,
    ratio: resolveProjectionRatioOnSegment(activePoint, nextPoint, latlng, ctx),
    previewTargets: [activePoint, nextPoint]
  }
}

const resolveInsertionContext = function (waypoints, activePoint, latlng, ctx) {
  const points = Array.isArray(waypoints) ? waypoints : []
  if (!points.length || !latlng || !ctx) return null
  const activeContext = resolveActiveInsertionContext(points, activePoint, latlng, ctx)
  if (activeContext) return activeContext
  const placement = ctx.fnResolveInsertPlacementForLatLng(points, latlng)
  if (!placement) return null
  const startPoint = placement.startPoint || points[Math.max(0, placement.insertIndex - 1)] || null
  const endPoint = placement.endPoint || points[Math.min(points.length - 1, placement.insertIndex)] || null
  const previewTargets = [startPoint, endPoint].filter(function (point, index, arr) {
    return !!point && arr.indexOf(point) === index
  })
  const previewAngleDeg = resolvePreviewAngleDeg(previewTargets, latlng, ctx)
  if (previewTargets.length === 2 && Number.isFinite(previewAngleDeg)) {
    const targetIndexes = previewTargets.map(function (point) { return points.indexOf(point) })
    if (previewAngleDeg >= INSERT_PREVIEW_RELEASE_ANGLE_MIN_DEG) {
      setInsertPreviewDebugState({
        angleDeg: previewAngleDeg,
        mode: 'route-insert',
        targetIndexes: targetIndexes
      })
      return {
        insertIndex: placement.insertIndex,
        basePoint: startPoint || endPoint || points[0] || null,
        segmentStart: startPoint,
        segmentEnd: endPoint,
        ratio: placement.t,
        previewTargets: [],
        angleMode: 'route-insert'
      }
    }
    if (previewAngleDeg < INSERT_PREVIEW_SINGLE_TARGET_ANGLE_MAX_DEG) {
      const startLatLng = getWaypointLeafletLatLng(startPoint, ctx)
      const endLatLng = getWaypointLeafletLatLng(endPoint, ctx)
      if (startLatLng && endLatLng) {
        const cursorLatLng = ctx.L.latLng(latlng.lat, latlng.lng)
        const distToStart = ctx.map.distance(cursorLatLng, startLatLng)
        const distToEnd = ctx.map.distance(cursorLatLng, endLatLng)
        const selectedTarget = distToStart <= distToEnd ? startPoint : endPoint
        const insertAfterTarget = selectedTarget === endPoint
        setInsertPreviewDebugState({
          angleDeg: previewAngleDeg,
          mode: 'single-target-0',
          targetIndexes: targetIndexes,
          selectedTargetIndex: points.indexOf(selectedTarget)
        })
        return buildSingleTargetInsertionContext(
          points,
          selectedTarget,
          insertAfterTarget,
          latlng,
          ctx,
          {
            segmentStart: startPoint,
            segmentEnd: endPoint
          }
        )
      }
    }
    setInsertPreviewDebugState({
      angleDeg: previewAngleDeg,
      mode: 'dual-preview',
      targetIndexes: targetIndexes
    })
  }
  return {
    insertIndex: placement.insertIndex,
    basePoint: startPoint || endPoint || points[0] || null,
    segmentStart: startPoint,
    segmentEnd: endPoint,
    ratio: placement.t,
    previewTargets: previewTargets
  }
}

const resolvePreviewInsertionContextForClick = function (waypoints, activePoint, latlng, ctx) {
  const points = Array.isArray(waypoints) ? waypoints : []
  if (!points.length || !latlng || !ctx || !ctx.map) return null
  if (shiftPreviewInsertionContext && shiftPreviewCursorLatLng) {
    const previewPoint = ctx.map.latLngToLayerPoint(shiftPreviewCursorLatLng)
    const clickPoint = ctx.map.latLngToLayerPoint(latlng)
    const dx = Number(previewPoint.x) - Number(clickPoint.x)
    const dy = Number(previewPoint.y) - Number(clickPoint.y)
    const distPx = Math.sqrt((dx * dx) + (dy * dy))
    if (distPx <= INSERT_PREVIEW_CLICK_SNAP_PX) {
      return shiftPreviewInsertionContext
    }
  }
  return resolveInsertionContext(points, activePoint, latlng, ctx)
}

const setInsertPreviewDebugState = function (payload) {
  lastInsertPreviewDebug = Object.assign({
    angleDeg: null,
    mode: 'none',
    targetIndexes: [],
    selectedTargetIndex: null,
    usePrevSide: null
  }, payload || {})
}

const cloneLatLngLiteral = function (latlng) {
  if (!latlng) return null
  const lat = Number(latlng.lat)
  const lng = Number(latlng.lng)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat: lat, lng: lng }
}

const getGridSnapHelper = function () {
  if (typeof window === 'undefined') return null
  const helper = window.PORTAL_GRID_HELPER
  if (!helper || typeof helper !== 'object') return null
  if (typeof helper.getNearestGridPoint !== 'function' || typeof helper.getVisibilityState !== 'function') return null
  return helper
}

const isGridSnapEnabledForManualDraw = function () {
  if (typeof window === 'undefined') return true
  const getter = window.PORTAL_GET_MAP_CURSOR_ELEVATION_GRID_SNAP_ENABLED
  if (typeof getter === 'function') return getter() !== false
  return true
}

const resolveGridSnappedLatLng = function (latlng, ctx) {
  if (!latlng || !ctx || !ctx.L) return latlng || null
  if (!isGridSnapEnabledForManualDraw()) return latlng
  const gridHelper = getGridSnapHelper()
  if (!gridHelper) return latlng
  const visibilityState = gridHelper.getVisibilityState()
  if (!visibilityState || visibilityState.isVisible !== true) return latlng
  const nearestGridPoint = gridHelper.getNearestGridPoint(latlng)
  if (!nearestGridPoint || !nearestGridPoint.latLng) return latlng
  const distancePx = Number(nearestGridPoint.distancePx)
  if (!Number.isFinite(distancePx) || distancePx > INSERT_PREVIEW_GRID_SNAP_PX) return latlng
  return ctx.L.latLng(nearestGridPoint.latLng.lat, nearestGridPoint.latLng.lng)
}

const buildInsertPreviewDebugPayload = function (ctx, latlng) {
  const payloadBase = {
    angleDeg: null,
    mode: 'none',
    targetIndexes: [],
    selectedTargetIndex: null,
    usePrevSide: null
  }
  if (!ctx || !ctx.state || !ctx.map) {
    return Object.assign({}, payloadBase, { mode: 'missing-context' })
  }
  const state = ctx.state
  const waypoints = state.importMissionState && Array.isArray(state.importMissionState.waypoints)
    ? state.importMissionState.waypoints
    : []
  if (!waypoints.length) {
    return Object.assign({}, payloadBase, { mode: 'no-waypoints' })
  }
  const probeLatLng = cloneLatLngLiteral(latlng || shiftPreviewCursorLatLng || lastInsertProbeLatLng)
  if (!probeLatLng) {
    return Object.assign({}, payloadBase, { mode: 'no-probe-latlng' })
  }
  const activePoint = state.importActivePoint || null
  const insertionContext = resolveInsertionContext(waypoints, activePoint, probeLatLng, ctx)
  if (!insertionContext) {
    return Object.assign({}, payloadBase, {
      mode: 'no-insertion-context',
      probeLatLng: probeLatLng,
      activePointIndex: waypoints.indexOf(activePoint)
    })
  }
  const previewTargets = Array.isArray(insertionContext.previewTargets) ? insertionContext.previewTargets.filter(Boolean) : []
  const previewAngleDeg = resolvePreviewAngleDeg(previewTargets, probeLatLng, ctx)
  const payload = Object.assign({}, payloadBase, {
    angleDeg: Number.isFinite(previewAngleDeg) ? previewAngleDeg : null,
    targetIndexes: previewTargets.map(function (point) { return waypoints.indexOf(point) }),
    probeLatLng: probeLatLng,
    activePointIndex: waypoints.indexOf(activePoint),
    insertIndex: Number(insertionContext.insertIndex)
  })
  if (insertionContext.angleMode === 'single-target') {
    payload.mode = 'single-target-0'
    payload.selectedTargetIndex = previewTargets.length ? waypoints.indexOf(previewTargets[0]) : null
    return payload
  }
  if (previewTargets.length === 2 && Number.isFinite(previewAngleDeg)) {
    payload.mode = 'dual-preview'
    return payload
  }
  payload.mode = 'route-insert'
  return payload
}

const dumpInsertPreviewDebug = function () {
  const payload = buildInsertPreviewDebugPayload(missionRenderCtx, shiftPreviewCursorLatLng || lastInsertProbeLatLng)
  lastInsertPreviewDebug = payload
  console.log('[insert-preview:dump]', payload)
  return payload
}

if (typeof window !== 'undefined') {
  window.PORTAL_DUMP_INSERT_PREVIEW_DEBUG = dumpInsertPreviewDebug
}

const syncActiveWaypointState = function (state) {
  const waypoints = state && state.importMissionState && Array.isArray(state.importMissionState.waypoints)
    ? state.importMissionState.waypoints
    : []
  const nextActivePoint = (state && state.importActivePoint && waypoints.includes(state.importActivePoint))
    ? state.importActivePoint
    : (waypoints.find(function (point) { return !!(point && point._isActivePoint) }) || null)
  if (state) state.importActivePoint = nextActivePoint
  waypoints.forEach(function (point) {
    point._isActivePoint = point === nextActivePoint
  })
  return nextActivePoint
}

const setActiveWaypoint = function (state, nextActivePoint, ctx) {
  const waypoints = state && state.importMissionState && Array.isArray(state.importMissionState.waypoints)
    ? state.importMissionState.waypoints
    : []
  if (state) state.importActivePoint = nextActivePoint || null
  waypoints.forEach(function (point) {
    const shouldBeActive = point === nextActivePoint
    const changed = !!point._isActivePoint !== shouldBeActive
    point._isActivePoint = shouldBeActive
    if (changed && ctx && typeof ctx.fnRefreshWaypointMarker === 'function') {
      ctx.fnRefreshWaypointMarker(point)
    }
  })
}

const insertMissionPointAtLatLng = function (missionState, labelPrefix, latlng, insertionContext, ctx) {
  const waypoints = missionState && Array.isArray(missionState.waypoints) ? missionState.waypoints : []
  if (!waypoints.length || !latlng || !insertionContext || !insertionContext.basePoint) return false
  const ratio = Number.isFinite(Number(insertionContext.ratio)) ? Number(insertionContext.ratio) : null
  const insertIndexRaw = Number(insertionContext.insertIndex)
  const insertIndex = Number.isInteger(insertIndexRaw)
    ? Math.max(0, Math.min(insertIndexRaw, waypoints.length))
    : waypoints.length
  const segmentStart = insertionContext.segmentStart || null
  const segmentEnd = insertionContext.segmentEnd || null
  const startHeightRaw = segmentStart ? ctx.fnGetPointNumericByPathAliases(segmentStart, ctx.POINT_HEIGHT_PATH_ALIASES) : null
  const endHeightRaw = segmentEnd ? ctx.fnGetPointNumericByPathAliases(segmentEnd, ctx.POINT_HEIGHT_PATH_ALIASES) : null
  const startExecuteRaw = segmentStart ? ctx.fnGetPointNumericByPathAliases(segmentStart, ctx.POINT_EXECUTE_HEIGHT_PATH_ALIASES) : null
  const endExecuteRaw = segmentEnd ? ctx.fnGetPointNumericByPathAliases(segmentEnd, ctx.POINT_EXECUTE_HEIGHT_PATH_ALIASES) : null
  const startProjectHeightRaw = segmentStart ? ctx.toFiniteNumber(ctx._.get(segmentStart, 'props.' + ctx.NON_EXPORT_POINT_HEIGHT_PATH)) : null
  const endProjectHeightRaw = segmentEnd ? ctx.toFiniteNumber(ctx._.get(segmentEnd, 'props.' + ctx.NON_EXPORT_POINT_HEIGHT_PATH)) : null
  const splitHeightRaw = (ratio === null) ? null : ctx.interpolateByRatioNullable(startHeightRaw, endHeightRaw, ratio)
  const splitExecuteRaw = (ratio === null) ? null : ctx.interpolateByRatioNullable(startExecuteRaw, endExecuteRaw, ratio)
  const splitProjectHeightRaw = (ratio === null) ? null : ctx.interpolateByRatioNullable(startProjectHeightRaw, endProjectHeightRaw, ratio)
  const altitudeOverride = (splitHeightRaw !== null) ? splitHeightRaw : splitExecuteRaw
  const newPoint = ctx.fnCreateInsertedPoint(insertionContext.basePoint, latlng, altitudeOverride)
  if (!newPoint) return false
  if (splitProjectHeightRaw !== null) {
    ctx.fnSetByPath(newPoint.props, ctx.NON_EXPORT_POINT_HEIGHT_PATH, ctx.roundToTenth(splitProjectHeightRaw))
  }
  if (splitHeightRaw !== null) {
    ctx.fnSetPointValueByPathAliases(newPoint, ctx.POINT_HEIGHT_PATH_ALIASES, ctx.roundToTenth(splitHeightRaw))
  }
  if (splitExecuteRaw !== null) {
    ctx.fnSetPointValueByPathAliases(newPoint, ctx.POINT_EXECUTE_HEIGHT_PATH_ALIASES, ctx.roundToTenth(splitExecuteRaw))
  }
  waypoints.splice(insertIndex, 0, newPoint)
  ctx.fnRenameWaypointsLinear(waypoints, { start: 1, prefix: 'S', syncDescription: true })
  ctx.fnSyncMissionPlacemarkOrder()
  renderImportLines([missionState.lineCoords || []], waypoints, ctx.state.importKmlFilename || labelPrefix || 'mission', missionState, ctx)
  ctx.fnSelectClear()
  return true
}

const normalizeShadowKey = function (rawKey) {
  const key = String(rawKey || '').trim().toUpperCase()
  return SHADOW_KEY_ORDER.includes(key) ? key : SHADOW_CENTER_KEY
}

const cloneShadowHistory = function (history) {
  return {
    undo: Array.isArray(history && history.undo) ? history.undo.slice() : [],
    redo: Array.isArray(history && history.redo) ? history.redo.slice() : []
  }
}

const buildShadowSnapshotFromMission = function (missionType, missionState) {
  const snapshot = captureImportStateSnapshot(missionType, missionState)
  if (!snapshot || typeof snapshot !== 'object') return null
  const missionTypeNormalized = snapshot.missionType === 'kmz' ? 'kmz' : 'kml'
  const missionXml = (typeof snapshot.missionXml === 'string' && snapshot.missionXml.trim()) ? snapshot.missionXml : null
  if (!missionXml) return null
  const missionWaylinesXml = (typeof snapshot.missionWaylinesXml === 'string' && snapshot.missionWaylinesXml.trim())
    ? snapshot.missionWaylinesXml
    : null
  if (missionTypeNormalized === 'kmz' && !missionWaylinesXml) return null
  return {
    missionType: missionTypeNormalized,
    missionXml: missionXml,
    missionWaylinesXml: missionWaylinesXml,
    pointProjectProps: Array.isArray(snapshot.pointProjectProps) ? snapshot.pointProjectProps.slice() : null
  }
}

const cloneShadowBundle = function (bundle) {
  if (!bundle || typeof bundle !== 'object') return null
  const out = {
    activeKey: normalizeShadowKey(bundle.activeKey),
    variants: {},
    histories: {}
  }
  const variants = (bundle && typeof bundle.variants === 'object') ? bundle.variants : {}
  const histories = (bundle && typeof bundle.histories === 'object') ? bundle.histories : {}
  SHADOW_KEY_ORDER.forEach((key) => {
    if (variants[key] && typeof variants[key] === 'object') out.variants[key] = variants[key]
    if (histories[key] && typeof histories[key] === 'object') out.histories[key] = cloneShadowHistory(histories[key])
  })
  return out
}

const toShadowBundleMeta = function (bundle) {
  const cloned = cloneShadowBundle(bundle)
  if (!cloned) return null
  return {
    activeKey: cloned.activeKey,
    variants: cloned.variants
  }
}

const ensureShadowBundle = function (state, missionState) {
  if (!missionState || typeof missionState !== 'object') return null
  if (missionState.__shadowBundle && typeof missionState.__shadowBundle === 'object') return missionState.__shadowBundle
  const snapshot = buildShadowSnapshotFromMission(state.importMissionType, missionState)
  missionState.__shadowBundle = {
    activeKey: SHADOW_CENTER_KEY,
    variants: {
      K: snapshot
    },
    histories: {
      K: cloneShadowHistory(state.importHistory)
    }
  }
  return missionState.__shadowBundle
}

const serializeCurrentHistory = function (state, ctx) {
  const normalize = (ctx && typeof ctx.fnNormalizeHistoryStack === 'function') ? ctx.fnNormalizeHistoryStack : function (stack) {
    return Array.isArray(stack) ? stack.slice() : []
  }
  return {
    undo: normalize(state.importHistory && state.importHistory.undo),
    redo: normalize(state.importHistory && state.importHistory.redo)
  }
}

const snapshotCurrentVariantIntoBundle = function (state, ctx) {
  const missionState = state.importMissionState
  if (!missionState) return
  const bundle = ensureShadowBundle(state, missionState)
  if (!bundle) return
  const activeKey = normalizeShadowKey(bundle.activeKey)
  const minimalSnapshot = buildShadowSnapshotFromMission(state.importMissionType, {
    xmlDoc: missionState.xmlDoc,
    secondaryXmlDoc: missionState.secondaryXmlDoc,
    waypoints: missionState.waypoints
  })
  if (minimalSnapshot) {
    bundle.variants[activeKey] = minimalSnapshot
  }
  bundle.histories[activeKey] = serializeCurrentHistory(state, ctx)
}

const readShadowActiveKey = function (missionState) {
  if (!missionState || !missionState.__shadowBundle) return SHADOW_CENTER_KEY
  return normalizeShadowKey(missionState.__shadowBundle.activeKey)
}

const resolveExistingShadowVariantKeys = function (missionState) {
  if (!missionState || !missionState.xmlDoc) return []
  const bundle = missionState.__shadowBundle
  if (!bundle || !bundle.variants || typeof bundle.variants !== 'object') return [SHADOW_CENTER_KEY]
  return SHADOW_KEY_ORDER.filter((key) => !!bundle.variants[key])
}

const resolveMiniActionRequestedVariants = function (action) {
  const normalized = String(action || '').trim().toUpperCase()
  if (normalized === SHADOW_LEFT_KEY) return [SHADOW_LEFT_KEY]
  if (normalized === SHADOW_CENTER_KEY) return [SHADOW_CENTER_KEY]
  if (normalized === SHADOW_RIGHT_KEY) return [SHADOW_RIGHT_KEY]
  if (normalized === (SHADOW_LEFT_KEY + SHADOW_RIGHT_KEY)) return [SHADOW_LEFT_KEY, SHADOW_RIGHT_KEY]
  if (normalized === (SHADOW_LEFT_KEY + SHADOW_CENTER_KEY + SHADOW_RIGHT_KEY)) return [SHADOW_LEFT_KEY, SHADOW_CENTER_KEY, SHADOW_RIGHT_KEY]
  return []
}

const buildMiniActionTooltipText = function (requestedKeys) {
  const keys = Array.isArray(requestedKeys) ? requestedKeys : []
  if (keys.length === 1 && keys[0] === SHADOW_CENTER_KEY) {
    return window.PORTAL_T ? window.PORTAL_T('project.menu.tooltip.default') : 'Művelet a következővel: Középső útvonal.'
  }
  const labels = keys
    .map((key) => {
      if (key === SHADOW_LEFT_KEY) return window.PORTAL_T ? window.PORTAL_T('project.menu.variant.left') : 'Bal'
      if (key === SHADOW_RIGHT_KEY) return window.PORTAL_T ? window.PORTAL_T('project.menu.variant.right') : 'Jobb'
      return ''
    })
    .filter(Boolean)
  if (!labels.length) {
    return window.PORTAL_T ? window.PORTAL_T('project.menu.tooltip.default') : 'Művelet a következővel: Középső útvonal.'
  }
  const joined = labels.length === 1
    ? labels[0]
    : (labels.length === 2
      ? (labels[0] + ' és ' + labels[1])
      : (labels.slice(0, -1).join(', ') + ' és ' + labels[labels.length - 1]))
  const sideText = labels.length === 1
    ? (joined + ' oldali útvonal')
    : (joined + ' oldali útvonal')
  return window.PORTAL_TF
    ? window.PORTAL_TF('project.menu.tooltip.with_variants', { variants: sideText })
    : ('Művelet a következővel: ' + sideText + '.')
}

const updateProjectMenuMiniActionState = function ($body, missionState) {
  if (!$body || typeof $body.find !== 'function') return
  const existing = resolveExistingShadowVariantKeys(missionState)
  $body.find('[data-leaf-export-kmz-action],[data-leaf-close-project-action]').each(function () {
    const action = String(
      this.getAttribute('data-leaf-export-kmz-action')
      || this.getAttribute('data-leaf-close-project-action')
      || ''
    ).trim().toUpperCase()
    const requested = resolveMiniActionRequestedVariants(action)
    const enabled = requested.length > 0 && requested.every((key) => existing.includes(key))
    const tooltipText = buildMiniActionTooltipText(requested)
    this.disabled = !enabled
    this.setAttribute('aria-disabled', enabled ? 'false' : 'true')
    this.setAttribute('data-portal-menu-enabled', enabled ? '1' : '0')
    this.setAttribute('data-bs-title', tooltipText)
    this.setAttribute('title', tooltipText)
    const tooltipInstance = window.bootstrap && window.bootstrap.Tooltip
      ? window.bootstrap.Tooltip.getInstance(this)
      : null
    if (tooltipInstance) {
      tooltipInstance.dispose()
    }
  })
}

const updateShadowToolbarVisualState = function ($body, missionState) {
  if (!$body || typeof $body.find !== 'function') return
  const bundle = missionState && missionState.__shadowBundle
  const activeKey = readShadowActiveKey(missionState)
  const existingCount = SHADOW_KEY_ORDER.filter((key) => !!(bundle && bundle.variants && bundle.variants[key])).length
  $body.find('[data-leaf-shadow-next]').prop('disabled', existingCount <= 1)
  $body.find('[data-leaf-shadow],[data-leaf-shadow-next]').attr('data-shadow-active-key', activeKey)
  updateProjectMenuMiniActionState($body, missionState)
}

const buildShadowExportFilename = function (baseName, variantKey, momentRef, explicitStamp) {
  const stampRaw = String(explicitStamp || '').trim()
  const stamp = stampRaw || (function () {
    const m = (typeof momentRef === 'function') ? momentRef() : momentRef
    return (m && typeof m.format === 'function') ? m.format('YYMMDD_HHmm') : String(Date.now())
  })()
  return String(baseName || 'export') + '_[' + normalizeShadowKey(variantKey) + ']_' + stamp + '.kmz'
}

const resolveShadowCycleTarget = function (bundle, activeKey, step) {
  const existing = SHADOW_KEY_ORDER.filter((key) => !!(bundle && bundle.variants && bundle.variants[key]))
  if (!existing.length) return null
  const currentIdx = existing.indexOf(activeKey)
  const baseIdx = currentIdx === -1 ? 0 : currentIdx
  const nextIdx = (baseIdx + step + existing.length) % existing.length
  return existing[nextIdx]
}

const parseMissionFromSnapshot = function (snapshot, ctx) {
  if (!snapshot || typeof snapshot !== 'object') return null
  const missionType = snapshot.missionType === 'kmz' ? 'kmz' : 'kml'
  const parsedMission = missionType === 'kmz'
    ? ctx.fnParseKmzMission(snapshot.missionXml, snapshot.missionWaylinesXml)
    : ctx.fnParseKmlMission(snapshot.missionXml)
  applyPointProjectPropsToWaypoints(snapshot.pointProjectProps, parsedMission && parsedMission.waypoints)
  return parsedMission
}

const buildInactiveVariantLayers = function (bundle, activeKey, ctx) {
  const layers = []
  if (!bundle || !bundle.variants) return layers
  SHADOW_KEY_ORDER.forEach((variantKey) => {
    if (variantKey === activeKey) return
    const snapshot = bundle.variants[variantKey]
    if (!snapshot) return
    const mission = parseMissionFromSnapshot(snapshot, ctx)
    const points = (mission && Array.isArray(mission.waypoints)) ? mission.waypoints : []
    if (!points.length) return
    const latlngs = points.map((pt) => {
      if (!pt || !Array.isArray(pt.latlng)) return null
      const lat = Number(pt.latlng[0])
      const lng = Number(pt.latlng[1])
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
      return ctx.L.latLng(lat, lng)
    }).filter(Boolean)
    if (latlngs.length >= 2) {
      layers.push(decorateLeafletPathDualDash(ctx.L.polyline(latlngs, {
        color: '#9ca3af',
        weight: 4,
        opacity: 0.7,
        dashArray: '6,4'
      }), { L: ctx.L }))
    }
    latlngs.forEach((latlng) => {
      layers.push(ctx.L.circleMarker(latlng, {
        radius: 4,
        color: '#6b7280',
        weight: 1,
        fillColor: '#9ca3af',
        fillOpacity: 0.85,
        interactive: false
      }))
    })
  })
  return layers
}

const switchShadowVariant = function (targetKey, ctx, options) {
  const state = ctx.state
  const opts = Object.assign({ route3dDelayMs: 0, forceRerender: false }, options || {})
  const missionState = state.importMissionState
  if (!missionState) return false
  const bundle = ensureShadowBundle(state, missionState)
  if (!bundle) return false
  const nextKey = normalizeShadowKey(targetKey)
  if (!bundle.variants[nextKey]) return false
  const currentKey = normalizeShadowKey(bundle.activeKey)
  if (currentKey === nextKey && !opts.forceRerender) return true

  snapshotCurrentVariantIntoBundle(state, ctx)
  const parsedMission = parseMissionFromSnapshot(bundle.variants[nextKey], ctx)
  if (!parsedMission) return false

  const rendered = renderImportLines([parsedMission.lineCoords], parsedMission.waypoints || [], state.importKmlFilename || 'mission', {
    xmlDoc: parsedMission.xmlDoc,
    documentEl: parsedMission.documentEl,
    folderEl: parsedMission.folderEl,
    importPlacemarkEl: parsedMission.importPlacemarkEl,
    importCoordEl: parsedMission.importCoordEl,
    secondaryXmlDoc: parsedMission.secondaryXmlDoc,
    secondaryDocumentEl: parsedMission.secondaryDocumentEl,
    secondaryFolderEl: parsedMission.secondaryFolderEl,
    polygons: parsedMission.polygons,
    shadowActiveKey: nextKey,
    shadowBundle: toShadowBundleMeta(bundle),
    shadowRoute3dDelayMs: opts.route3dDelayMs
  }, ctx)
  if (!rendered) return false
  state.importMissionState.__shadowBundle = bundle
  state.importMissionState.__shadowBundle.activeKey = nextKey
  state.importHistory = cloneShadowHistory(bundle.histories[nextKey])
  updateShadowToolbarVisualState(ctx.$body, state.importMissionState)
  ctx.fnSchedulePersistImportDraft()
  return true
}

const setByPathLocal = function (obj, path, value) {
  if (!obj || typeof obj !== 'object') return
  const parts = String(path || '').split('.').filter(Boolean)
  if (!parts.length) return
  let cursor = obj
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i]
    if (!cursor[key] || typeof cursor[key] !== 'object') cursor[key] = {}
    cursor = cursor[key]
  }
  cursor[parts[parts.length - 1]] = value
}

const buildShadowVariantSnapshot = function (sourceSnapshot, variantKey, payload, ctx) {
  const mission = parseMissionFromSnapshot(sourceSnapshot, ctx)
  if (!mission || !Array.isArray(mission.waypoints) || !mission.waypoints.length) return null
  const axisUpBearing = Number(payload.axisUpBearing)
  const projectHeight = Number(payload.projectHeight)
  const leftPitch = Number(payload.leftPitch)
  const rightPitch = Number(payload.rightPitch)
  if (!Number.isFinite(axisUpBearing) || !Number.isFinite(projectHeight) || !(projectHeight > 0)) return null
  const sideKey = normalizeShadowKey(variantKey)
  if (![SHADOW_LEFT_KEY, SHADOW_RIGHT_KEY].includes(sideKey)) return null
  const pitch = sideKey === SHADOW_LEFT_KEY ? leftPitch : rightPitch
  const absPitch = Math.abs(pitch)
  if (!(absPitch > 0.0001 && absPitch < 89.9999)) return null
  const offsetMeters = projectHeight / Math.tan(absPitch * (Math.PI / 180))
  // Shadow displacement must run along the drawn axis direction (not perpendicular).
  const shiftBearing = sideKey === SHADOW_LEFT_KEY ? ((axisUpBearing + 180) % 360) : axisUpBearing
  const headingToCenter = sideKey === SHADOW_LEFT_KEY ? axisUpBearing : ((axisUpBearing + 180) % 360)

  ;(mission.waypoints || []).forEach((pt) => {
    if (!pt || !Array.isArray(pt.latlng)) return
    const moved = offsetLatLngByMeters(pt.latlng[0], pt.latlng[1], offsetMeters, shiftBearing)
    pt.latlng = [moved.lat, moved.lng]
    if (!pt.props || typeof pt.props !== 'object') pt.props = {}
    if (!pt.props.project || typeof pt.props.project !== 'object') pt.props.project = {}
    pt.props.project.height = projectHeight
    setByPathLocal(pt.props, 'wpml:gimbalPitchAngle', pitch)
    setByPathLocal(pt.props, 'wpml:waypointHeadingParam.wpml:waypointHeadingAngle', headingToCenter)
    setByPathLocal(pt.props, 'ExtendedData.mis:heading', headingToCenter)
    ctx.fnUpdatePointCoordinates(pt, moved.lat, moved.lng)
    ctx.fnUpdatePointXmlByPath(pt, 'wpml:gimbalPitchAngle', pitch)
    ctx.fnUpdatePointXmlByPath(pt, 'wpml:waypointHeadingParam.wpml:waypointHeadingAngle', headingToCenter)
    ctx.fnUpdatePointXmlByPath(pt, 'ExtendedData.mis:heading', headingToCenter)
  })
  return buildShadowSnapshotFromMission(sourceSnapshot.missionType, mission)
}

const ensureShadowBindings = function (ctx) {
  latestMissionCtx = ctx
  if (shadowBindingsInstalled || !ctx || !ctx.$body) return
  shadowBindingsInstalled = true
  const $body = ctx.$body
  $body.on('click', '[data-leaf-shadow-next]', function () {
    const localCtx = latestMissionCtx
    if (!localCtx || !localCtx.state || !localCtx.state.importMissionState) return
    const missionState = localCtx.state.importMissionState
    const bundle = ensureShadowBundle(localCtx.state, missionState)
    if (!bundle) return
    const targetKey = resolveShadowCycleTarget(bundle, readShadowActiveKey(missionState), +1)
    if (!targetKey) return
    switchShadowVariant(targetKey, localCtx, { route3dDelayMs: 2000 })
  })

  document.addEventListener('portal:shadow-create', function (event) {
    const localCtx = latestMissionCtx
    if (!localCtx || !localCtx.state || !localCtx.state.importMissionState) return
    const payload = (event && event.detail) ? event.detail : {}
    const state = localCtx.state
    const missionState = state.importMissionState
    const bundle = ensureShadowBundle(state, missionState)
    if (!bundle) return
    snapshotCurrentVariantIntoBundle(state, localCtx)
    const sourceKey = readShadowActiveKey(missionState)
    const sourceSnapshot = bundle.variants[sourceKey]
    if (!sourceSnapshot) return
    if (payload.includeLeft) {
      const leftSnapshot = buildShadowVariantSnapshot(sourceSnapshot, SHADOW_LEFT_KEY, payload, localCtx)
      if (leftSnapshot) {
        bundle.variants[SHADOW_LEFT_KEY] = leftSnapshot
        bundle.histories[SHADOW_LEFT_KEY] = { undo: [], redo: [] }
      }
    }
    if (payload.includeRight) {
      const rightSnapshot = buildShadowVariantSnapshot(sourceSnapshot, SHADOW_RIGHT_KEY, payload, localCtx)
      if (rightSnapshot) {
        bundle.variants[SHADOW_RIGHT_KEY] = rightSnapshot
        bundle.histories[SHADOW_RIGHT_KEY] = { undo: [], redo: [] }
      }
    }
    let target = sourceKey
    if (payload.includeLeft && bundle.variants[SHADOW_LEFT_KEY]) target = SHADOW_LEFT_KEY
    if (payload.includeRight && !payload.includeLeft && bundle.variants[SHADOW_RIGHT_KEY]) target = SHADOW_RIGHT_KEY
    if (payload.includeLeft && payload.includeRight && bundle.variants[SHADOW_RIGHT_KEY]) target = SHADOW_RIGHT_KEY
    switchShadowVariant(target, localCtx)
  })
}

const renderImportLines = function (importLines, waypoints, labelPrefix, meta, ctx) {
  if (!hasMissionRenderLayerContext(ctx)) return false
  ensureShadowBindings(ctx)
  const _ = ctx._
  const $ = ctx.$
  const L = ctx.L
  const state = ctx.state
  const polygons = (meta && Array.isArray(meta.polygons)) ? meta.polygons : []
  const shadowBundleMeta = (meta && meta.shadowBundle && typeof meta.shadowBundle === 'object')
    ? meta.shadowBundle
    : ((meta && meta.__shadowBundle && typeof meta.__shadowBundle === 'object') ? meta.__shadowBundle : null)
  const shadowActiveKey = normalizeShadowKey((meta && meta.shadowActiveKey) || (meta && meta.__shadowActiveKey) || SHADOW_CENTER_KEY)
  const shadowRoute3dDelayMs = Math.max(0, Math.floor(Number((meta && meta.shadowRoute3dDelayMs) || 0)))
  if (!importLines.length && !waypoints.length && !polygons.length) return
  state.importMissionState = {
    xmlDoc: meta && meta.xmlDoc ? meta.xmlDoc : null,
    documentEl: meta && meta.documentEl ? meta.documentEl : null,
    folderEl: meta && meta.folderEl ? meta.folderEl : null,
    importPlacemarkEl: meta && meta.importPlacemarkEl ? meta.importPlacemarkEl : null,
    importCoordEl: meta && meta.importCoordEl ? meta.importCoordEl : null,
    secondaryXmlDoc: meta && meta.secondaryXmlDoc ? meta.secondaryXmlDoc : null,
    secondaryDocumentEl: meta && meta.secondaryDocumentEl ? meta.secondaryDocumentEl : null,
    secondaryFolderEl: meta && meta.secondaryFolderEl ? meta.secondaryFolderEl : null,
    waypoints: waypoints || [],
    lineCoords: importLines || [],
    polygons: polygons,
    __shadowActiveKey: shadowActiveKey
  }
  if (shadowBundleMeta && typeof shadowBundleMeta === 'object') {
    state.importMissionState.__shadowBundle = cloneShadowBundle(shadowBundleMeta)
  }
  syncActiveWaypointState(state)
  if (state.importMissionType === 'kmz') {
    ctx.fnSyncMissionPlacemarkOrder()
  }
  ctx.importItems.clearLayers()
  ctx.importLineItems.clearLayers()
  state.importMissionState.polygons.forEach((poly, idx) => {
    if (!poly || !Array.isArray(poly.cord) || poly.cord.length < 3) return
    const polyStyle = _.assign({
      color: '#3388ff',
      weight: 2,
      opacity: 1,
      fillColor: '#3388ff',
      fillOpacity: 0.2
    }, poly.style || {})
    const layer = decorateLeafletPathDualDash(L.polygon(poly.cord, polyStyle), { L: L })
    const tooltipLabel = poly.label || (labelPrefix || ctx.fnT('layer.import')) + ' Polygon ' + (idx + 1)
    layer.bindTooltip(tooltipLabel, { direction: 'center', className: 'portal-map-label', sticky: true })
    ctx.fnBindPolygonCreateClick(layer)
    ctx.importItems.addLayer(layer)
  })
  if (state.importMissionState.waypoints.length) {
    const coords = state.importMissionState.waypoints.map(pt => pt.latlng)
    const style = ctx.fnStyle(['Polyline', 'import', 0], {})
    style.color = SHADOW_COLOR_BY_KEY[shadowActiveKey] || SHADOW_COLOR_BY_KEY[SHADOW_CENTER_KEY]
    style.opacity = 1
    style.weight = 6
    const layer = L.polyline(coords, style)
    layer.bindTooltip((labelPrefix || ctx.fnT('layer.import')), { direction: 'center', className: 'portal-map-label', sticky: true })
    layer.on('add', function () {
      ctx.fnSyncAddPointCursor()
    })
    layer.on('click', function (evt) {
      if (!state.importAddPointMode && !state.importDragMode) return
      if (!state.importMissionState || !state.importMissionState.waypoints || !state.importMissionState.waypoints.length) return
      if (!evt || !evt.latlng) return
      const cursorLatLng = resolveGridSnappedLatLng(evt.latlng, ctx)
      const historyBefore = ctx.fnCaptureState()
      const waypoints = state.importMissionState.waypoints
      const insertionContext = resolvePreviewInsertionContextForClick(
        waypoints,
        state.importActivePoint && state.importAddPointMode ? state.importActivePoint : null,
        cursorLatLng,
        ctx
      )
      if (!insertMissionPointAtLatLng(state.importMissionState, labelPrefix, cursorLatLng, insertionContext, ctx)) return
      ctx.fnHistoryPush(historyBefore)
    })
    ctx.importLineItems.addLayer(layer)
    state.importMissionState.polyline = layer
    ctx.fnSyncAddPointCursor()
  }
  const inactiveLayers = buildInactiveVariantLayers(state.importMissionState.__shadowBundle, shadowActiveKey, ctx)
  inactiveLayers.forEach((layer) => {
    ctx.importLineItems.addLayer(layer)
  })
  const totalWaypoints = state.importMissionState.waypoints.length
  const activeWaypointColor = SHADOW_COLOR_BY_KEY[shadowActiveKey] || SHADOW_COLOR_BY_KEY[SHADOW_CENTER_KEY]
  const fnWaypointColor = function (index) {
    if (!totalWaypoints) return activeWaypointColor
    return activeWaypointColor
  }
  state.importMissionState.waypoints.forEach((pt, index) => {
    pt.idx = index
    const color = fnWaypointColor(index)
    const marker = L.marker(pt.latlng, {
      draggable: state.importDragMode,
      icon: ctx.fnBuildWaypointIcon(pt, color)
    })
    marker._customStyle = { color: '#111', weight: 2, fillColor: color, fillOpacity: 0.6 }
    marker.bindTooltip(ctx.fnGetWaypointTooltipText(pt), { direction: 'top', className: 'portal-map-label portal-map-label--point', sticky: true })
    ctx.fnRegisterImportPoint(pt, marker)
    $(marker).on('click', function (evt) {
      if (state.importSelectMode || (evt && evt.originalEvent && (evt.originalEvent.ctrlKey || (evt.originalEvent.shiftKey && !state.importAddPointMode && !state.importDragMode)))) {
        ctx.fnSelectToggle(pt)
        return
      }
      if (state.importAddPointMode || state.importDragMode) {
        setActiveWaypoint(state, pt, ctx)
        return
      }
      ctx.fnSelectClear()
      const title = (labelPrefix ? labelPrefix + ' - ' : '') + 'WP ' + (pt.idx + 1)
      ctx.fnSwalProps(title, pt, function (coords) {
        if (!coords || Number.isNaN(coords.lat) || Number.isNaN(coords.lng)) return
        pt.latlng = [coords.lat, coords.lng]
        if (pt.props && pt.props.coordinates) {
          pt.props.coordinates.lat = coords.lat
          pt.props.coordinates.lng = coords.lng
        }
        marker.setLatLng(pt.latlng)
        ctx.fnUpdatePointCoordinates(pt, coords.lat, coords.lng)
        ctx.fnRefreshImportLine()
      }, function (change) {
        const missionAltContext = ctx.fnGetMissionAltitudeContextFor3d(state.importMissionState)
        if (change && change.path) {
          const pathLower = String(change.path).toLowerCase()
          const hasWaypointHeightEdit = ctx.fnIsWaypointHeightPath(pathLower)
          if (hasWaypointHeightEdit) {
            ctx.fnRecalculateEgm96EllipsoidForPoints([pt], missionAltContext)
          }
          if (ctx.fnIsEllipsoidHeightPath(pathLower)) {
            ctx.fnSyncPointCachedEgm96Offset(pt)
          }
          if (pathLower !== ctx.NON_EXPORT_POINT_HEIGHT_PATH && (pathLower.includes('coordinates.alt') || pathLower.includes('executeheight') || pathLower.includes('ellipsoidheight') || pathLower.endsWith('.height') || pathLower === 'height' || pathLower.includes('wpml:height'))) {
            const resolvedAltitude = ctx.fnGetPointAltitudeFor3d(pt, ctx.fnGetPreferredPointDemValue(pt), missionAltContext)
            if (resolvedAltitude !== null) {
              pt.altitude = resolvedAltitude
            }
            ctx.fnScheduleRoute3dSharedSync()
          }
        }
        if (change && change.path === 'ExtendedData.mis:heading') {
          ctx.fnRefreshWaypointMarker(pt)
        }
        if (change && pt.marker && typeof pt.marker.setTooltipContent === 'function') {
          pt.marker.setTooltipContent(ctx.fnGetWaypointTooltipText(pt))
        }
        if (change) {
          ctx.fnScheduleRoute3dSharedSync()
        }
      })
    })
    marker.on('dragstart', function () {
      marker._dragSnapshot = ctx.fnCaptureState()
      // Store original line as ghost/reference - show where the line was before moving
      const originalCoords = (waypoints || []).map(wp => wp.latlng)
      if (originalCoords.length > 1) {
        marker._originalPolyline = decorateLeafletPathDualDash(ctx.L.polyline(originalCoords, {
          color: '#000000',
          weight: 1,
          opacity: 0.5,
          dashArray: '5, 5',
          className: 'portal-import-line--ghost'
        }), { L: ctx.L })
        marker._originalPolyline.addTo(ctx.map)
      }
      // Identify adjacent waypoints for live distance display
      const allWps = state.importMissionState ? (state.importMissionState.waypoints || []) : []
      const ptIdx = allWps.indexOf(pt)
      marker._dragPrevPt = ptIdx > 0 ? allWps[ptIdx - 1] : null
      marker._dragNextPt = ptIdx < allWps.length - 1 ? allWps[ptIdx + 1] : null
    })
    marker.on('drag', function () {
      // During drag: update point position and show line preview in real-time
      // This allows continuous visual feedback of where the line will go
      const ll = marker.getLatLng()
      pt.latlng = [ll.lat, ll.lng]
      ctx.fnRefreshImportLine()
      // Update live distance labels for adjacent segments
      dragDistanceLabels.forEach(function (lbl) { ctx.map.removeLayer(lbl) })
      dragDistanceLabels = []
      const makeDragLabel = function (aLL, bLL) {
        const distM = Number(ctx.map.distance(aLL, bLL) || 0)
        const midLL = ctx.L.latLng((aLL.lat + bLL.lat) / 2, (aLL.lng + bLL.lng) / 2)
        const lbl = ctx.L.marker(midLL, {
          icon: ctx.L.divIcon({
            className: 'portal-drag-distance-label',
            html: distM.toFixed(2) + ' m',
            iconSize: [0, 0],
            iconAnchor: [0, 0]
          }),
          interactive: false,
          keyboard: false
        })
        lbl.addTo(ctx.map)
        dragDistanceLabels.push(lbl)
      }
      if (marker._dragPrevPt && Array.isArray(marker._dragPrevPt.latlng)) {
        makeDragLabel(ll, ctx.L.latLng(marker._dragPrevPt.latlng[0], marker._dragPrevPt.latlng[1]))
      }
      if (marker._dragNextPt && Array.isArray(marker._dragNextPt.latlng)) {
        makeDragLabel(ll, ctx.L.latLng(marker._dragNextPt.latlng[0], marker._dragNextPt.latlng[1]))
      }
    })
    marker.on('dragend', function () {
      const ll = marker.getLatLng()
      pt.latlng = [ll.lat, ll.lng]
      if (!pt.props || typeof pt.props !== 'object') pt.props = {}
      if (!pt.props.coordinates || typeof pt.props.coordinates !== 'object') pt.props.coordinates = {}
      pt.props.coordinates.lat = ll.lat
      pt.props.coordinates.lng = ll.lng
      const pointCoordinatesText = ctx.fnBuildPointCoordinatesText(pt.props)
      if (ctx.fnTemplateSupportsPointCoordinatesField()) {
        ctx.fnSetByPath(pt.props, 'Point.coordinates', pointCoordinatesText)
      }
      ctx.fnUpdatePointCoordinates(pt, ll.lat, ll.lng)
      if (ctx.fnTemplateSupportsPointCoordinatesField()) {
        ctx.fnUpdatePointXmlByPath(pt, 'Point.coordinates', pointCoordinatesText)
      }
      ctx.fnRefreshImportLine()
      // Remove the ghost/original line overlay
      if (marker._originalPolyline) {
        ctx.map.removeLayer(marker._originalPolyline)
        marker._originalPolyline = null
      }
      // Remove drag distance labels
      dragDistanceLabels.forEach(function (lbl) { ctx.map.removeLayer(lbl) })
      dragDistanceLabels = []
      marker._dragPrevPt = null
      marker._dragNextPt = null
      setActiveWaypoint(state, pt, ctx)
      const snap = marker._dragSnapshot
      marker._dragSnapshot = null
      if (snap) ctx.fnHistoryPush(snap)
    })
    ctx.importLineItems.addLayer(marker)
  })
  if (shadowRoute3dDelayMs > 0) {
    ctx.fnUpdateMissionLineCoords()
    setTimeout(function () {
      ctx.fnScheduleRoute3dSharedSync()
    }, shadowRoute3dDelayMs)
  } else {
    ctx.fnRefreshImportLine()
  }
  updateShadowToolbarVisualState(ctx.$body, state.importMissionState)
  // Install add-point dashed preview handler (once per map)
  missionRenderCtx = ctx
  if (!shiftHandlerInstalled) {
    shiftHandlerInstalled = true
    ctx.map.on('mousemove', function (evt) {
      if (!missionRenderCtx) return
      if (evt && evt.latlng) {
        lastInsertProbeLatLng = cloneLatLngLiteral(evt.latlng)
      }
      const s = missionRenderCtx.state
      if (!s.importAddPointMode && !s.importDragMode) { clearShiftPreview(); return }
      const allWps = s.importMissionState ? (s.importMissionState.waypoints || []) : []
      if (!allWps.length) { clearShiftPreview(); return }
      const cursorLatLng = resolveGridSnappedLatLng(evt && evt.latlng, missionRenderCtx)
      if (!cursorLatLng) { clearShiftPreview(); return }
      const insertionContext = resolveInsertionContext(
        allWps,
        s.importActivePoint,
        cursorLatLng,
        missionRenderCtx
      )
      if (!insertionContext || !Array.isArray(insertionContext.previewTargets) || !insertionContext.previewTargets.length) {
        clearShiftPreview()
        return
      }
      clearShiftPreviewVisuals()
      shiftPreviewInsertionContext = insertionContext
      shiftPreviewCursorLatLng = cursorLatLng
      shiftPreviewTargets = insertionContext.previewTargets.slice()
      const cursorLL = cursorLatLng
      const labelRows = []
      const addPreviewLine = function (targetPoint) {
        const ptLL = getWaypointLeafletLatLng(targetPoint, missionRenderCtx)
        if (!ptLL) return
        const distM = Number(missionRenderCtx.map.distance(cursorLatLng, ptLL) || 0)
        const line = decorateLeafletPathDualDash(ctx.L.polyline([cursorLL, ptLL], {
          color: '#000000',
          weight: 2,
          opacity: 0.85,
          dashArray: '5, 5',
          interactive: false,
          className: 'portal-import-line--shift-preview'
        }), { L: ctx.L })
        line.addTo(missionRenderCtx.map)
        shiftPreviewLayers.push(line)
        labelRows.push({
          cursorLL: cloneLatLngLiteral(cursorLL),
          ptLL: cloneLatLngLiteral(ptLL),
          text: distM.toFixed(2) + ' m'
        })
      }
      shiftPreviewTargets.forEach(addPreviewLine)
      scheduleShiftPreviewLabelRender({ rows: labelRows })
    })
    ctx.map.on('click', function (evt) {
      if (!missionRenderCtx) return
      const s = missionRenderCtx.state
      if (!s.importAddPointMode && !s.importDragMode) return
      const cursorLatLng = resolveGridSnappedLatLng(evt && evt.latlng, missionRenderCtx)
      if (!cursorLatLng) return
      const localCtx = missionRenderCtx
      const missionState = s.importMissionState
      const waypoints = missionState ? (missionState.waypoints || []) : []
      if (!waypoints.length) return
      const insertionContext = resolvePreviewInsertionContextForClick(
        waypoints,
        s.importActivePoint,
        cursorLatLng,
        localCtx
      )
      if (!insertionContext) return
      const historyBefore = localCtx.fnCaptureState()
      if (!insertMissionPointAtLatLng(missionState, 'mission', cursorLatLng, insertionContext, localCtx)) return
      clearShiftPreview()
      localCtx.fnHistoryPush(historyBefore)
    })
  }
  return true
}

const setMissionWaypointLatLng = function (point, lat, lng, ctx) {
  if (!point) return
  point.latlng = [lat, lng]
  if (!point.props || typeof point.props !== 'object') {
    point.props = {}
  }
  if (!point.props.coordinates || typeof point.props.coordinates !== 'object') {
    point.props.coordinates = {}
  }
  point.props.coordinates.lat = lat
  point.props.coordinates.lng = lng
  if (point.altitude !== undefined && point.altitude !== null) {
    point.props.coordinates.alt = point.altitude
  }
  ctx.fnUpdatePointCoordinates(point, lat, lng)
  const pointCoordinatesText = ctx.fnBuildPointCoordinatesText(point.props || {})
  if (ctx.fnTemplateSupportsPointCoordinatesField()) {
    ctx.fnUpdatePointXmlByPath(point, 'Point.coordinates', pointCoordinatesText)
  }
}

const placeMissionWaypointsInViewport = function (parsedMission, ctx) {
  if (!ctx.map || !parsedMission || !Array.isArray(parsedMission.waypoints) || parsedMission.waypoints.length < 2) return false
  const size = ctx.map.getSize()
  if (!size || !(size.x > 20) || !(size.y > 20)) return false
  const centerX = size.x / 2
  const centerY = size.y / 2
  const halfSpanX = Math.max(40, size.x * 0.15)
  const margin = 24
  const startPoint = ctx.L.point(ctx.clamp(centerX - halfSpanX, margin, size.x - margin), ctx.clamp(centerY, margin, size.y - margin))
  const endPoint = ctx.L.point(ctx.clamp(centerX + halfSpanX, margin, size.x - margin), ctx.clamp(centerY, margin, size.y - margin))
  const startLatLng = ctx.map.containerPointToLatLng(startPoint)
  const endLatLng = ctx.map.containerPointToLatLng(endPoint)
  const first = parsedMission.waypoints[0]
  const last = parsedMission.waypoints[parsedMission.waypoints.length - 1]
  setMissionWaypointLatLng(first, startLatLng.lat, startLatLng.lng, ctx)
  setMissionWaypointLatLng(last, endLatLng.lat, endLatLng.lng, ctx)
  parsedMission.lineCoords = [first.latlng, last.latlng]
  if (parsedMission.importCoordEl) {
    const firstCoordText = ctx.fnBuildPointCoordinatesText(first.props || {})
    const lastCoordText = ctx.fnBuildPointCoordinatesText(last.props || {})
    parsedMission.importCoordEl.textContent = firstCoordText + ' ' + lastCoordText
  }
  return true
}

const loadMissionFromParsedMission = function (parsedMission, filename, options, ctx) {
  if (!parsedMission || !hasMissionRenderLayerContext(ctx)) return false
  const opts = ctx._.assign({
    fitBounds: true,
    forceDragMode: false,
    missionType: 'kml'
  }, options || {})
  const state = ctx.state
  const hasWaypoints = !!(parsedMission.waypoints && parsedMission.waypoints.length)
  ctx.fnSelectClear()
  ctx.importItems.clearLayers()
  ctx.importLineItems.clearLayers()
  state.importMissionState = null
  state.importMissionType = (opts.missionType === 'kmz') ? 'kmz' : 'kml'
  state.importKmlFilename = filename || (state.importMissionType === 'kmz' ? 'mission.kmz' : 'mission.kml')
  state.templateWaypointDefaultsCache = null
  const rendered = renderImportLines([parsedMission.lineCoords], parsedMission.waypoints || [], state.importKmlFilename, {
    xmlDoc: parsedMission.xmlDoc,
    documentEl: parsedMission.documentEl,
    folderEl: parsedMission.folderEl,
    importPlacemarkEl: parsedMission.importPlacemarkEl,
    importCoordEl: parsedMission.importCoordEl,
    secondaryXmlDoc: parsedMission.secondaryXmlDoc,
    secondaryDocumentEl: parsedMission.secondaryDocumentEl,
    secondaryFolderEl: parsedMission.secondaryFolderEl,
    polygons: parsedMission.polygons,
    shadowActiveKey: normalizeShadowKey(parsedMission && parsedMission.__shadowActiveKey),
    shadowBundle: parsedMission && parsedMission.__shadowBundle ? parsedMission.__shadowBundle : null
  }, ctx)
  if (!rendered) return false
  if (state.importMissionType === 'kmz') {
    if (ctx.fnEnsureKmzTimestampsOnMissionLoad(state.importMissionState)) {
      ctx.fnScheduleRoute3dSharedSync()
    }
  }
  const loadedContext = ctx.fnGetMissionAltitudeContextFor3d(state.importMissionState)
  if (ctx.fnRecalculateEgm96EllipsoidForPoints(state.importMissionState.waypoints || [], loadedContext) > 0) {
    ctx.fnScheduleRoute3dSharedSync()
  }
  if (opts.fitBounds && (ctx.importItems.getLayers().length || ctx.importLineItems.getLayers().length)) {
    const boundsGroup = ctx.L.featureGroup([ctx.importItems, ctx.importLineItems])
    ctx.map.fitBounds(boundsGroup.getBounds())
  }
  if (hasWaypoints) {
    ctx.$body.find('.btn-portal--mod2').addClass('btn-portal--active')
  } else {
    ctx.$body.find('.btn-portal--mod2').removeClass('btn-portal--active')
  }
  if (hasWaypoints && opts.forceDragMode) {
    state.importAddPointMode = true
    state.importDragMode = true
    ctx.$body.find('[data-leaf-add-point]').addClass('active')
    ctx.fnSetWaypointDragging(true)
    ctx.fnSyncAddPointCursor()
  }
  const bundle = ensureShadowBundle(state, state.importMissionState)
  if (bundle) {
    bundle.activeKey = normalizeShadowKey((parsedMission && parsedMission.__shadowActiveKey) || bundle.activeKey)
    if (!bundle.variants[bundle.activeKey]) {
      bundle.variants[bundle.activeKey] = (typeof ctx.fnNormalizeStateSnapshot === 'function')
        ? ctx.fnNormalizeStateSnapshot(ctx.fnCaptureState())
        : ctx.fnCaptureState()
    }
    if (!bundle.histories[bundle.activeKey]) {
      bundle.histories[bundle.activeKey] = { undo: [], redo: [] }
    }
    state.importHistory = cloneShadowHistory(bundle.histories[bundle.activeKey])
  } else {
    state.importHistory = { undo: [], redo: [] }
  }
  updateShadowToolbarVisualState(ctx.$body, state.importMissionState)
  ctx.fnSchedulePersistImportDraft()
  return hasWaypoints
}

const closeEditedMission = function (ctx) {
  const state = ctx.state
  ctx.fnSelectClear()
  if (ctx.importItems && typeof ctx.importItems.clearLayers === 'function') {
    ctx.importItems.clearLayers()
  }
  if (ctx.importLineItems && typeof ctx.importLineItems.clearLayers === 'function') {
    ctx.importLineItems.clearLayers()
  }
  state.importMissionState = null
  state.importKmlFilename = null
  state.importMissionType = 'kml'
  state.templateWaypointDefaultsCache = null
  state.importHistory = { undo: [], redo: [] }
  state.importHistoryLock = false
  state.importSelectMode = false
  state.importAddPointMode = false
  state.importDragMode = false
  state.importActivePoint = null
  state.importSeekMode = false
  state.importSeekDrawActive = false
  state.importSeekDrawLine = null
  state.importSeekAxisCallback = null
  if (state.importSelectDrawRect) state.importSelectDrawRect.disable()
  if (state.importSelectDrawPoly) state.importSelectDrawPoly.disable()
  clearShiftPreview()
  ctx.$body.find('[data-leaf-select-square]').removeClass('active')
  ctx.$body.find('[data-leaf-select-poly]').removeClass('active')
  ctx.$body.find('[data-leaf-add-point]').removeClass('active')
  ctx.$body.find('[data-leaf-seek]').removeClass('active')
  ctx.$body.find('.btn-portal--mod2').removeClass('btn-portal--active')
  ctx.$body.find('[data-leaf-shadow-next]').prop('disabled', true)
  updateProjectMenuMiniActionState(ctx.$body, null)
  ctx.fnScheduleRoute3dSharedSync()
  void ctx.fnClearPersistedImportDraft()
}

const closeMissionVariants = function (variantKeys, ctx) {
  const state = ctx.state
  const missionState = state.importMissionState
  if (!missionState || !missionState.xmlDoc) return false
  const bundle = ensureShadowBundle(state, missionState)
  if (!bundle) return false
  snapshotCurrentVariantIntoBundle(state, ctx)

  const requested = Array.isArray(variantKeys) ? variantKeys.map((key) => normalizeShadowKey(key)) : []
  const uniqueRequested = Array.from(new Set(requested))
  const existing = SHADOW_KEY_ORDER.filter((key) => !!(bundle.variants && bundle.variants[key]))
  const deletable = uniqueRequested.filter((key) => existing.includes(key))
  if (!deletable.length) return false
  if (deletable.length >= existing.length) {
    closeEditedMission(ctx)
    return true
  }

  const activeBefore = readShadowActiveKey(missionState)
  const activeDeleted = deletable.includes(activeBefore)
  if (activeDeleted) {
    const startIdx = SHADOW_KEY_ORDER.indexOf(activeBefore)
    let nextKey = null
    for (let i = 1; i <= SHADOW_KEY_ORDER.length; i += 1) {
      const idx = (startIdx + i + SHADOW_KEY_ORDER.length) % SHADOW_KEY_ORDER.length
      const key = SHADOW_KEY_ORDER[idx]
      if (!deletable.includes(key) && existing.includes(key)) {
        nextKey = key
        break
      }
    }
    if (nextKey) {
      switchShadowVariant(nextKey, ctx, { route3dDelayMs: 0 })
    }
  }

  const refreshedMission = state.importMissionState
  const refreshedBundle = ensureShadowBundle(state, refreshedMission)
  if (!refreshedBundle) return false
  deletable.forEach((key) => {
    if (refreshedBundle.variants && Object.prototype.hasOwnProperty.call(refreshedBundle.variants, key)) {
      delete refreshedBundle.variants[key]
    }
    if (refreshedBundle.histories && Object.prototype.hasOwnProperty.call(refreshedBundle.histories, key)) {
      delete refreshedBundle.histories[key]
    }
  })
  const remaining = SHADOW_KEY_ORDER.filter((key) => !!(refreshedBundle.variants && refreshedBundle.variants[key]))
  if (!remaining.length) {
    closeEditedMission(ctx)
    return true
  }
  if (!remaining.includes(readShadowActiveKey(state.importMissionState))) {
    refreshedBundle.activeKey = remaining[0]
  }
  switchShadowVariant(refreshedBundle.activeKey, ctx, { route3dDelayMs: 0, forceRerender: true })
  ctx.fnSchedulePersistImportDraft()
  return true
}

const restoreImportDraftFromIndexedDb = async function (ctx) {
  const windowRef = ctx.windowRef || window
  if (!windowRef.idbKeyval) return false
  if (!hasMissionRenderLayerContext(ctx)) return false
  try {
    const draft = await windowRef.idbKeyval.get(ctx.IMPORT_DRAFT_KEY)
    if (!draft || typeof draft !== 'object') return false
    const current = ctx.fnNormalizeStateSnapshot(draft.current)
    if (!current || !current.missionXml) return false
    const missionType = current.missionType === 'kmz' ? 'kmz' : 'kml'
    const parsedMission = missionType === 'kmz'
      ? ctx.fnParseKmzMission(current.missionXml, current.missionWaylinesXml)
      : ctx.fnParseKmlMission(current.missionXml)
    applyPointProjectPropsToWaypoints(current.pointProjectProps, parsedMission && parsedMission.waypoints)
    if (current.shadowBundle && typeof current.shadowBundle === 'object') {
      parsedMission.__shadowBundle = cloneShadowBundle(current.shadowBundle)
      parsedMission.__shadowActiveKey = normalizeShadowKey(current.shadowBundle.activeKey)
    }
    const loaded = loadMissionFromParsedMission(parsedMission, draft.importKmlFilename || (missionType === 'kmz' ? 'mission.kmz' : 'mission.kml'), {
      fitBounds: false,
      forceDragMode: false,
      missionType: missionType
    }, ctx)
    if (!loaded) return false
    const history = (draft.history && typeof draft.history === 'object') ? draft.history : {}
    ctx.state.importHistory = {
      undo: ctx.fnNormalizeHistoryStack(history.undo),
      redo: ctx.fnNormalizeHistoryStack(history.redo)
    }
    const restoredMission = ctx.state.importMissionState
    const restoredBundle = restoredMission && restoredMission.__shadowBundle
    if (restoredBundle && typeof restoredBundle === 'object') {
      const activeKey = readShadowActiveKey(restoredMission)
      if (!restoredBundle.histories || typeof restoredBundle.histories !== 'object') {
        restoredBundle.histories = {}
      }
      restoredBundle.histories[activeKey] = cloneShadowHistory(ctx.state.importHistory)
      updateShadowToolbarVisualState(ctx.$body, restoredMission)
    }
    ctx.fnSchedulePersistImportDraft()
    return true
  } catch (error) {
    console.error(ctx.fnT('log.indexeddb_draft_restore_error'), error)
    return false
  }
}

const fnParseCoordinateFromPlacemark = function (placemarkEl) {
  if (!placemarkEl || typeof placemarkEl.getElementsByTagName !== 'function') return null
  const pointEl = placemarkEl.getElementsByTagName('Point')[0]
  if (!pointEl) return null
  const coordinateEl = pointEl.getElementsByTagName('coordinates')[0]
  if (!coordinateEl) return null
  const raw = String(coordinateEl.textContent || '').trim()
  if (!raw) return null
  const firstCoord = raw.split(/\s+/).map((item) => String(item || '').trim()).filter(Boolean)[0] || ''
  const parts = firstCoord.split(',').map((item) => String(item || '').trim())
  if (parts.length < 2) return null
  const lng = Number(parts[0])
  const lat = Number(parts[1])
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat: lat, lng: lng }
}

const fnPointNeedsCoordinateSync = function (point, lat, lng) {
  const targetLat = Number(lat)
  const targetLng = Number(lng)
  if (!Number.isFinite(targetLat) || !Number.isFinite(targetLng)) return false
  const nodes = []
  if (point && point.xmlEl) nodes.push(point.xmlEl)
  if (point && Array.isArray(point.linkedXmlEls)) {
    point.linkedXmlEls.forEach((node) => {
      if (node) nodes.push(node)
    })
  }
  if (!nodes.length) return false
  const EPS = 1e-12
  for (let i = 0; i < nodes.length; i += 1) {
    const parsed = fnParseCoordinateFromPlacemark(nodes[i])
    if (!parsed) return true
    if (Math.abs(parsed.lat - targetLat) > EPS || Math.abs(parsed.lng - targetLng) > EPS) {
      return true
    }
  }
  return false
}

const fnFormatExportProgressText = function (ctx, key, fallbackText, vars) {
  const fnTF = (ctx && typeof ctx.fnTF === 'function') ? ctx.fnTF : null
  if (fnTF) {
    const translated = String(fnTF(key, vars || {}) || '').trim()
    if (translated && translated !== key) return translated
  }
  const text = String(fallbackText || '').trim()
  if (!text) return ''
  return text
    .replace(/\{current\}/g, String((vars && vars.current) || 0))
    .replace(/\{total\}/g, String((vars && vars.total) || 0))
    .replace(/\{percent\}/g, String((vars && vars.percent) || 0))
}

const exportCurrentMission = async function (targetType, ctx) {
  const state = ctx.state
  ensureShadowBindings(ctx)
  const request = (targetType && typeof targetType === 'object')
    ? targetType
    : { type: targetType }
  const normalizedType = String(request.type || '').trim().toLowerCase()
  const requestedVariants = Array.isArray(request.variants) ? request.variants.map((key) => normalizeShadowKey(key)) : []
  const internalNoSwitch = request.internalNoSwitch === true
  const requestStamp = String(request.stamp || '').trim()
  const missionState = state.importMissionState
  if (!missionState || !missionState.xmlDoc || !missionState.waypoints || !missionState.waypoints.length) {
    ctx.Swal.fire(ctx.fnT('export.no_data'))
    return false
  }
  const normalizedTarget = normalizedType
  const isKmz = normalizedTarget
    ? (normalizedTarget === 'kmz')
    : (state.importMissionType === 'kmz')

  if (isKmz && requestedVariants.length > 1 && !internalNoSwitch) {
    snapshotCurrentVariantIntoBundle(state, ctx)
    const missionBundle = ensureShadowBundle(state, state.importMissionState)
    if (!missionBundle) return false
    const originKey = readShadowActiveKey(state.importMissionState)
    for (let i = 0; i < requestedVariants.length; i += 1) {
      const variantKey = requestedVariants[i]
      if (!missionBundle.variants[variantKey]) {
        ctx.Swal.fire(ctx.fnT('export.shadow_missing_variant'))
        return false
      }
    }
    const sharedStamp = requestStamp || (function () {
      const m = (typeof ctx.momentRef === 'function') ? ctx.momentRef() : ctx.momentRef
      return (m && typeof m.format === 'function') ? m.format('YYMMDD_HHmm') : String(Date.now())
    })()
    for (let i = 0; i < requestedVariants.length; i += 1) {
      const variantKey = requestedVariants[i]
      if (!switchShadowVariant(variantKey, ctx)) {
        ctx.Swal.fire(ctx.fnT('export.shadow_missing_variant'))
        return false
      }
      const ok = await exportCurrentMission({
        type: 'kmz',
        variants: [variantKey],
        internalNoSwitch: true,
        stamp: sharedStamp
      }, ctx)
      if (!ok) {
        void switchShadowVariant(originKey, ctx)
        return false
      }
    }
    void switchShadowVariant(originKey, ctx)
    return true
  }

  if (isKmz && requestedVariants.length === 1 && !internalNoSwitch) {
    const targetKey = requestedVariants[0]
    const missionBundle = ensureShadowBundle(state, state.importMissionState)
    if (missionBundle && missionBundle.variants[targetKey]) {
      const switched = switchShadowVariant(targetKey, ctx)
      if (!switched) {
        ctx.Swal.fire(ctx.fnT('export.shadow_missing_variant'))
        return false
      }
    } else if (targetKey !== SHADOW_CENTER_KEY) {
      ctx.Swal.fire(ctx.fnT('export.shadow_missing_variant'))
      return false
    }
  }

  const windowRef = ctx.windowRef || window
  if (isKmz) {
    if (!windowRef.JSZip) {
      ctx.Swal.fire(ctx.fnT('export.kmz_missing_jszip'))
      return false
    }
    if (!missionState.secondaryXmlDoc) {
      ctx.Swal.fire(ctx.fnT('export.kmz_missing_waylines'))
      return false
    }
  }

  const totalWaypoints = missionState.waypoints.length
  const titleKey = isKmz ? 'export.progress_title_kmz' : 'export.progress_title_kml'
  const fallbackTitle = isKmz ? 'KMZ export' : 'KML export'
  const exportTask = startPortalLoadingTask({
    id: 'mission-export',
    title: ctx.fnT(titleKey) || fallbackTitle,
    status: ctx.fnT('export.progress_prepare') || 'Export preparation',
    progress: 0,
    mode: 'determinate'
  })

  const fnUpdatePointNormalizationProgress = function (current, total) {
    const percent = total > 0 ? Math.round((current / total) * 100) : 100
    exportTask.update({
      status: fnFormatExportProgressText(
        ctx,
        'export.progress_points',
        'Waypoint normalization: {current}/{total} ({percent}%).',
        { current: current, total: total, percent: percent }
      ),
      progress: 5 + ((total > 0 ? (current / total) : 1) * 55)
    })
  }

  try {
    const chunkSize = 80
    for (let index = 0; index < totalWaypoints; index += 1) {
      const pt = missionState.waypoints[index]
      if (pt && pt.xmlEl) {
        pt.idx = index
        if (fnPointNeedsCoordinateSync(pt, pt.latlng[0], pt.latlng[1])) {
          ctx.fnUpdatePointCoordinates(pt, pt.latlng[0], pt.latlng[1])
        }
        const nameEl = pt.xmlEl.getElementsByTagName('name')[0]
        if (nameEl && /^Waypoint\\d+$/i.test(String(nameEl.textContent || '').trim())) {
          nameEl.textContent = 'Waypoint' + String(index + 1)
        }
      }
      const processed = index + 1
      if ((processed % chunkSize) === 0 || processed === totalWaypoints) {
        fnUpdatePointNormalizationProgress(processed, totalWaypoints)
        if (processed < totalWaypoints) {
          await new Promise((resolve) => {
            setTimeout(resolve, 0)
          })
        }
      }
    }

    if (isKmz) {
      exportTask.update({
        status: ctx.fnT('export.progress_normalize_kmz') || 'KMZ normalization',
        progress: 65
      })
      ctx.fnNormalizeKmzMissionForExport()
    }
    ctx.fnSyncMissionPlacemarkOrder()
    ctx.fnUpdateMissionLineCoords()

    if (isKmz) {
      exportTask.update({
        status: ctx.fnT('export.progress_build_zip') || 'KMZ package build',
        progress: 72
      })
      const canonicalDocs = ctx.fnBuildKmzCanonicalExportDocs()
      const templateDocForExport = (canonicalDocs && canonicalDocs.templateDoc) ? canonicalDocs.templateDoc : missionState.xmlDoc
      const waylinesDocForExport = (canonicalDocs && canonicalDocs.waylinesDoc) ? canonicalDocs.waylinesDoc : missionState.secondaryXmlDoc
      const templateText = serializeXmlDocumentPretty(templateDocForExport)
      const waylinesText = serializeXmlDocumentPretty(waylinesDocForExport)
      const baseName = (state.importKmlFilename || 'export.kmz').replace(/\.kmz$/i, '')
      const missionBundle = ensureShadowBundle(state, state.importMissionState)
      const activeShadowKey = missionBundle ? readShadowActiveKey(state.importMissionState) : SHADOW_CENTER_KEY
      const fileName = buildShadowExportFilename(baseName, activeShadowKey, ctx.momentRef, requestStamp)
      const zip = new windowRef.JSZip()
      const zipFileOptions = {
        compression: 'DEFLATE',
        compressionOptions: { level: 9 },
        createFolders: false
      }
      zip.file('wpmz/template.kml', templateText, zipFileOptions)
      zip.file('wpmz/waylines.wpml', waylinesText, zipFileOptions)
      const blob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 9 }
      }, function (metadata) {
        const zipPercent = Number(metadata && metadata.percent)
        const safePercent = Number.isFinite(zipPercent) ? Math.max(0, Math.min(100, zipPercent)) : 0
        exportTask.update({
          status: ctx.fnT('export.progress_build_zip') || 'KMZ package build',
          progress: 72 + (safePercent * 0.26)
        })
      })
      if (windowRef.navigator && windowRef.navigator.msSaveOrOpenBlob) {
        windowRef.navigator.msSaveOrOpenBlob(blob, fileName)
      } else {
        const url = windowRef.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        a.style.display = 'none'
        document.body.appendChild(a)
        a.dispatchEvent(new MouseEvent('click'))
        document.body.removeChild(a)
        setTimeout(function () {
          windowRef.URL.revokeObjectURL(url)
        }, 1000)
      }
      exportTask.complete({
        status: ctx.fnT('export.progress_done') || 'Export completed',
        progress: 100
      })
      return true
    }

    exportTask.update({
      status: ctx.fnT('export.progress_serialize') || 'KML serialization',
      progress: 86
    })
    const serializer = new XMLSerializer()
    const kmlText = serializer.serializeToString(missionState.xmlDoc)
    const blob = new Blob([kmlText], { type: 'application/vnd.google-earth.kml+xml;charset=utf-8' })
    const m = (typeof ctx.momentRef === 'function') ? ctx.momentRef() : ctx.momentRef
    const stamp = (m && typeof m.format === 'function') ? m.format('YYYYMMDD_HHmmss') : String(Date.now())
    const baseName = (state.importKmlFilename || 'export.kml').replace(/\.kml$/i, '')
    const fileName = baseName + '_mod' + stamp + '.kml'
    if (windowRef.navigator && windowRef.navigator.msSaveOrOpenBlob) {
      windowRef.navigator.msSaveOrOpenBlob(blob, fileName)
    } else {
      const url = windowRef.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.style.display = 'none'
      document.body.appendChild(a)
      a.dispatchEvent(new MouseEvent('click'))
      document.body.removeChild(a)
      setTimeout(function () {
        windowRef.URL.revokeObjectURL(url)
      }, 1000)
    }
    exportTask.complete({
      status: ctx.fnT('export.progress_done') || 'Export completed',
      progress: 100
    })
    return true
  } catch (error) {
    if (isKmz) {
      console.error(ctx.fnT('log.kmz_export_error'), error)
      ctx.Swal.fire(ctx.fnT('export.kmz_failed'))
    } else {
      console.error(ctx.fnT('log.export_error') || 'Export error', error)
      ctx.Swal.fire(ctx.fnT('export.failed') || 'Export failed.')
    }
    exportTask.fail({
      status: ctx.fnT('export.progress_failed') || 'Export failed'
    })
    return false
  }
}

export {
  renderImportLines,
  setMissionWaypointLatLng,
  placeMissionWaypointsInViewport,
  dumpInsertPreviewDebug,
  loadMissionFromParsedMission,
  closeEditedMission,
  closeMissionVariants,
  restoreImportDraftFromIndexedDb,
  exportCurrentMission
}
